/**
 * Approvals dashboard — surfaces every pending approval across orders,
 * invoices, requisitions, and contracts.
 *
 * Two-pane layout:
 *   - "My approvals"   : items where the current user is the next approver.
 *                        Each row has Approve / Reject actions.
 *   - "Team approvals" : everything else still pending (read-only visibility).
 *
 * Backend: GET /api/approvals/dashboard
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Inbox, AlertTriangle, Users, Clock, DollarSign, CheckSquare, FileText,
  ShoppingCart, Receipt, ScrollText, ArrowRight, Check, X,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const KIND_META = {
  order:       { label: 'Purchase Order', icon: ShoppingCart, color: 'bg-indigo-50 text-indigo-700 border-indigo-200', endpoint: 'orders' },
  requisition: { label: 'Requisition',    icon: FileText,     color: 'bg-emerald-50 text-emerald-700 border-emerald-200', endpoint: 'requisitions' },
  invoice:     { label: 'Invoice',        icon: Receipt,      color: 'bg-amber-50 text-amber-700 border-amber-200', endpoint: 'invoices' },
  contract:    { label: 'Contract',       icon: ScrollText,   color: 'bg-violet-50 text-violet-700 border-violet-200', endpoint: 'contracts' },
};

const fmt$ = (v, ccy = 'USD') => `${ccy === 'USD' ? '$' : ccy + ' '}${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function StatCard({ icon: Icon, label, value, sub, accent, onClick, danger }) {
  return (
    <div
      onClick={onClick}
      className={`rounded-xl border p-5 transition-shadow ${onClick ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : 'hover:shadow-sm'} ${danger ? 'border-rose-200 bg-rose-50/30' : 'bg-card'}`}
      data-testid={`approvals-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider font-medium text-muted-foreground">{label}</div>
          <div className={`text-2xl font-bold mt-1 tabular-nums ${danger ? 'text-rose-700' : 'text-zinc-900'}`}>{value}</div>
          {sub && <div className="text-xs mt-1 text-muted-foreground">{sub}</div>}
        </div>
        <div className={`p-2 rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
    </div>
  );
}

export default function ApprovalsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [actioning, setActioning] = useState(null); // { item, action }
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/approvals/dashboard');
      setData(r.data);
    } catch { toast.error('Failed to load approvals'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const { item, action } = actioning;
    try {
      // Backend endpoint differs per kind
      const k = item.kind;
      const path = k === 'invoice' ? `/api/invoices/${encodeURIComponent(item.ref)}/${action}` :
                   k === 'requisition' ? `/api/requisitions/${encodeURIComponent(item.ref)}/${action}` :
                   k === 'contract' ? `/api/contracts/${encodeURIComponent(item.ref)}/${action}` :
                   `/api/orders/${encodeURIComponent(item.ref)}/${action}`;
      await api.post(path, { notes });
      toast.success(`${KIND_META[k].label} ${action}d`);
      setActioning(null); setNotes('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  if (loading) return <AppShell><div className="p-6"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div></AppShell>;

  const k = data?.kpis || {};
  const filt = (arr) => filter ? arr.filter(x => JSON.stringify(x).toLowerCase().includes(filter.toLowerCase())) : arr;
  const my = filt(data?.my_pending || []);
  const team = filt(data?.team_pending || []);

  return (
    <AppShell>
      <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="approvals-dashboard">
        <PageHeader title="Approvals" subtitle="Items awaiting your action and across the team" />

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Inbox} label="My pending" value={k.my_pending || 0}
            sub={`${k.my_overdue || 0} overdue · ${data?.kpis?.by_kind?.length ? data.kpis.by_kind.map(b => `${b.value} ${b.name}`).join(' · ') : ''}`}
            accent="bg-indigo-50 text-indigo-600" />
          <StatCard icon={AlertTriangle} label="Overdue (mine)" value={k.my_overdue || 0}
            sub={k.my_overdue ? 'Past SLA — action required' : 'Within SLA'} accent="bg-rose-50 text-rose-600"
            danger={k.my_overdue > 0} />
          <StatCard icon={Users} label="Team pending" value={k.team_pending || 0}
            sub={`${k.team_overdue || 0} overdue`} accent="bg-amber-50 text-amber-600" />
          <StatCard icon={DollarSign} label="Total value pending" value={fmt$(k.total_value_pending)}
            sub={`Oldest item: ${data?.kpis?.oldest_hours ? (data.kpis.oldest_hours / 24).toFixed(1) + ' days' : '—'}`}
            accent="bg-emerald-50 text-emerald-600" />
        </div>

        {/* Filter input */}
        <div className="flex items-center gap-3">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by reference, supplier, or amount…"
            className="max-w-md"
            data-testid="approvals-filter"
          />
          <Button variant="outline" onClick={load} size="sm">Refresh</Button>
        </div>

        {/* My approvals */}
        <ApprovalSection
          title="My approvals"
          subtitle="Awaiting your action — click Approve or Reject"
          items={my}
          actionable
          onAction={(item, action) => { setActioning({ item, action }); setNotes(''); }}
          navigate={navigate}
          emptyMsg="Nothing waiting on you. Inbox zero — nicely done."
        />

        {/* Team approvals */}
        <ApprovalSection
          title="Team approvals"
          subtitle="Pending across the rest of the organisation — visibility only"
          items={team}
          actionable={false}
          navigate={navigate}
          emptyMsg="No pending approvals across the team."
        />

        {/* Action dialog */}
        <Dialog open={!!actioning} onOpenChange={(o) => !o && setActioning(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actioning && (actioning.action === 'approve' ? `Approve ${KIND_META[actioning.item.kind]?.label} ${actioning.item.ref}` : `Reject ${KIND_META[actioning.item.kind]?.label} ${actioning.item.ref}`)}
              </DialogTitle>
            </DialogHeader>
            {actioning && (
              <div className="space-y-3">
                <div className="rounded-lg border bg-zinc-50 p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="font-mono">{actioning.item.ref}</span></div>
                  {actioning.item.supplier_name && <div className="flex justify-between"><span className="text-muted-foreground">Supplier / Title</span><span>{actioning.item.supplier_name || actioning.item.title}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono tabular-nums">{fmt$(actioning.item.amount, actioning.item.currency)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pending for</span><span>{actioning.item.age_label}</span></div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Notes (optional)</label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a note for the audit trail" data-testid="approvals-notes" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setActioning(null)}>Cancel</Button>
              <Button
                onClick={submit}
                className={actioning?.action === 'reject' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                data-testid="approvals-confirm"
              >
                {actioning?.action === 'approve' ? <><Check className="h-4 w-4 mr-1" /> Approve</> : <><X className="h-4 w-4 mr-1" /> Reject</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

function ApprovalSection({ title, subtitle, items, actionable, onAction, navigate, emptyMsg }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-5 py-3 border-b bg-zinc-50/60">
        <div className="text-sm font-semibold text-zinc-900">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">{emptyMsg}</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((item) => {
            const meta = KIND_META[item.kind] || KIND_META.order;
            const Icon = meta.icon;
            return (
              <div
                key={`${item.kind}-${item.ref}`}
                className={`grid grid-cols-[2.5rem_8rem_1fr_7rem_5.5rem_auto] items-center gap-4 px-5 py-3 transition-colors ${item.overdue ? 'bg-rose-50/30' : 'hover:bg-zinc-50/60'}`}
                data-testid={`approval-row-${item.kind}-${item.ref}`}
              >
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg border ${meta.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="font-mono text-sm font-semibold text-zinc-900 truncate">{item.ref || '—'}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{item.supplier_name || item.title || meta.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {meta.label}
                    {item.approver_name && !actionable && (<> · Waiting on <span className="font-medium">{item.approver_name}</span></>)}
                    {item.rule_name && (<> · {item.rule_name}</>)}
                  </div>
                </div>
                <div className="text-right font-mono tabular-nums text-sm">{fmt$(item.amount, item.currency)}</div>
                <div className="text-center">
                  <Badge className={item.overdue ? 'bg-rose-100 text-rose-700 hover:bg-rose-100' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-100'}>
                    <Clock className="h-3 w-3 mr-1" /> {item.age_label}
                    {item.overdue && ' overdue'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  {actionable ? (
                    <>
                      <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => onAction(item, 'reject')} data-testid={`approval-reject-${item.ref}`}>
                        <X className="h-4 w-4" />
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAction(item, 'approve')} data-testid={`approval-approve-${item.ref}`}>
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => navigate(item.route_to)}>
                      View <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
